/**
 * Security scans.
 *
 * WHAT A SCAN IS
 * A passive check of one domain's publicly published configuration: DNS, email
 * sender protection (SPF, DMARC, MX), the HTTPS certificate and HTTP security
 * headers. It does not probe for vulnerabilities, and nothing on this screen
 * may suggest that it does. A scan with no issues means those settings looked
 * right; it is not assurance that the domain is secure.
 *
 * WHAT THE DATA CAN AND CANNOT TELL US
 *   - Credits are charged when a schedule is CREATED, for every run in it.
 *     Running a scan that is already paid for ("Run now", "Retry") costs
 *     nothing more. The screen says which is which, every time.
 *   - The API has no "list schedules" route. Schedules are rebuilt from the
 *     scan rows that share a `configId`: a schedule is active while it has runs
 *     left to do. Its cadence is inferred from its run count (1 / 4 / 12) and is
 *     only shown when the loaded list is complete.
 *   - A failed request is NEVER rendered as "no scans" or "no findings". Every
 *     query has an error state with a retry, because in a compliance tool an
 *     error that looks like a clean result is a lie.
 *
 * Detail view lives at `/scans?scan=<id>`, so it is linkable and the browser's
 * back button works, without needing a second route.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, qs, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import {
  Card, CardHeader, Button, Pill, Modal, EmptyState, Skeleton, ErrorNotice, StatTile,
  Spinner, type Tone,
} from '../components/ui';
import { IconChevron, IconShield } from '../components/icons';
import { FindingsList, countBySeverity, type Finding } from '../components/scans/findings';
import { ScheduleScanModal, type Pricing, type ScheduleResult } from '../components/scans/ScheduleScanModal';
import { useScanT, useScanError, useCredits, type ScanKey } from '../components/scans/strings';

// --- Types --------------------------------------------------------------------

type ScanStatus = 'PENDING' | 'IN_PROGRESS' | 'READY' | 'FAILED';

interface ScanRow {
  id: string;
  vendorId: string | null;
  configId: string | null;
  domain: string;
  status: ScanStatus;
  scheduledFor: string;
  startedAt: string | null;
  completedAt: string | null;
  redCount: number;
  amberCount: number;
  greenCount: number;
  findingsJson: string | null;
  reportExpiresAt: string | null;
  errorMessage: string | null;
  vendor: { id: string; name: string; domain: string } | null;
}

interface ScanDetail extends ScanRow {
  findings: Finding[];
  reportExpired: boolean;
}

type RunResult =
  | { status: 'FAILED'; error: string }
  | { status: 'READY'; scan: ScanRow; findings: Finding[] };

type Notice =
  | { tone: 'success' | 'error'; text: string; detail?: string; action?: { label: string; onClick: () => void } };

const PAGE_SIZE = 100;
/** A scheduled run this close to now counts as due, to absorb clock skew. */
const DUE_SLACK_MS = 24 * 3600 * 1000;
/** A scan marked running for longer than this is probably interrupted. */
const STUCK_AFTER_MS = 10 * 60 * 1000;

const isDue = (s: ScanRow) =>
  s.status === 'PENDING' && new Date(s.scheduledFor).getTime() <= Date.now() + DUE_SLACK_MS;

const isStuck = (s: ScanRow) =>
  s.status === 'IN_PROGRESS' && !!s.startedAt && Date.now() - new Date(s.startedAt).getTime() > STUCK_AFTER_MS;

/** The findings payload was deleted by the retention job. */
const isExpired = (s: ScanRow) =>
  s.status === 'READY' && s.reportExpiresAt !== null
  && new Date(s.reportExpiresAt).getTime() < Date.now() && !s.findingsJson;

const STATUS_TONE: Record<ScanStatus, Tone> = {
  READY: 'neutral', PENDING: 'neutral', IN_PROGRESS: 'neutral', FAILED: 'serious',
};

// --- Entry ------------------------------------------------------------------------

export function ScansPage() {
  const tt = useScanT();
  const { allows } = useAuth();

  if (!allows('scan.read')) {
    return (
      <div className="oc-page">
        <EmptyState title={tt('scans.title')} description={tt('scans.noAccess')} />
      </div>
    );
  }
  return <ScansScreen />;
}

function ScansScreen() {
  const tt = useScanT();
  const credits = useCredits();
  const describeError = useScanError();
  const { i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const canSchedule = allows('scan.schedule');
  const scanId = params.get('scan');

  const [notice, setNotice] = useState<Notice | null>(null);
  const [dialog, setDialog] = useState<{ domain?: string; vendorId?: string | null } | null>(null);
  const [cancelGroup, setCancelGroup] = useState<ScheduleGroup | null>(null);

  const fmt = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
      : '—';
  const fmtDay = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  const list = useQuery({
    queryKey: ['scans', 'list'],
    queryFn: () => api.get<ScanRow[]>(`/scans${qs({ pageSize: PAGE_SIZE })}`),
    // Follow a running scan to its end without the user refreshing.
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'IN_PROGRESS') ? 5000 : false),
  });

  // Advisory only: the real price is always re-read inside the dialog.
  const pricing = useQuery({
    queryKey: ['scans', 'pricing', 'page'],
    queryFn: () => api.get<Pricing>('/scans/pricing'),
    enabled: canSchedule,
  });

  const rows = list.data ?? [];
  const truncated = rows.length >= PAGE_SIZE;

  // --- Actions ------------------------------------------------------------------

  const openScan = (id: string | null) => {
    setNotice(null);
    const next = new URLSearchParams(params);
    if (id) next.set('scan', id); else next.delete('scan');
    setParams(next);
  };

  const run = useMutation({
    mutationFn: (scan: ScanRow) => api.post<RunResult>(`/scans/${scan.id}/run`),
    onSuccess: (result, scan) => {
      if (result.status === 'FAILED') {
        setNotice({
          tone: 'error',
          text: tt('scans.notice.runFailed', { domain: scan.domain }),
          detail: tt('scans.notice.reason', { reason: result.error }),
        });
      } else {
        setNotice({
          tone: 'success',
          text: tt('scans.notice.runDoneCounts', {
            domain: scan.domain,
            summary: `${tt('scans.glance.critical', { count: result.scan.redCount })}, ${tt('scans.glance.warning', { count: result.scan.amberCount })}`,
          }),
          action: { label: tt('scans.notice.viewResults'), onClick: () => openScan(scan.id) },
        });
      }
    },
    onError: (err) => setNotice({ tone: 'error', text: describeError(err) }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['scans'] });
      void queryClient.invalidateQueries({ queryKey: ['credits'] });
    },
  });
  const runningId = run.isPending ? run.variables?.id ?? null : null;

  const cancel = useMutation({
    mutationFn: (g: ScheduleGroup) => api.delete(`/scans/schedule/${g.configId}`),
    onSuccess: (_r, g) => {
      setCancelGroup(null);
      setNotice({
        tone: 'success',
        text: tt('scans.notice.cancelled', { domain: g.domain, count: g.pending.length }),
      });
    },
    onError: (err) => {
      setCancelGroup(null);
      setNotice({ tone: 'error', text: describeError(err) });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['scans'] }),
  });

  const onScheduled = (result: ScheduleResult, domain: string) => {
    setDialog(null);
    void queryClient.invalidateQueries({ queryKey: ['scans'] });
    void queryClient.invalidateQueries({ queryKey: ['credits'] });
    // The first run of a new schedule is due immediately and already paid for:
    // offer to run it, rather than making the person wait for the scheduler.
    setNotice({
      tone: 'success',
      text: tt('scans.notice.scheduled', { credits: credits(result.creditsCharged), balance: result.balance }),
      action: {
        label: tt('scans.notice.runFirst'),
        onClick: async () => {
          try {
            const fresh = await queryClient.fetchQuery({
              queryKey: ['scans', 'list'],
              queryFn: () => api.get<ScanRow[]>(`/scans${qs({ pageSize: PAGE_SIZE })}`),
              staleTime: 0,
            });
            const first = fresh
              .filter((s) => s.configId === result.config.id && s.status === 'PENDING')
              .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor))[0];
            if (first) { setNotice(null); run.mutate(first); }
          } catch (err) {
            setNotice({ tone: 'error', text: describeError(err), detail: domain });
          }
        },
      },
    });
  };

  // --- Derived ------------------------------------------------------------------

  const groups = useMemo(() => buildGroups(rows, truncated), [rows, truncated]);

  const tiles = useMemo(() => {
    const ready = rows.filter((s) => s.status === 'READY');
    const latest = new Map<string, ScanRow>();
    for (const s of ready) {
      const cur = latest.get(s.domain);
      if (!cur || (s.completedAt ?? '') > (cur.completedAt ?? '')) latest.set(s.domain, s);
    }
    return {
      domains: latest.size,
      critical: [...latest.values()].filter((s) => s.redCount > 0).length,
      upcoming: rows.filter((s) => s.status === 'PENDING').length,
      failed: rows.filter((s) => s.status === 'FAILED').length,
    };
  }, [rows]);

  const unitPrice = pricing.data?.unitPrice ?? null;

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      {scanId && notice && <NoticeBar notice={notice} onDismiss={() => setNotice(null)} />}
      {scanId ? (
        <ScanDetailView
          id={scanId}
          fmt={fmt}
          canSchedule={canSchedule}
          onBack={() => openScan(null)}
          onRun={(s) => { setNotice(null); run.mutate(s); }}
          runningId={runningId}
          onScanAgain={(s) => setDialog({ domain: s.domain, vendorId: s.vendorId })}
        />
      ) : (
        <>
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="oc-h1">{tt('scans.title')}</h1>
              <p className="oc-muted mt-1">{tt('scans.subtitle')}</p>
            </div>
            {canSchedule && (
              <div className="flex flex-col items-start sm:items-end gap-1">
                <Button variant="primary" onClick={() => setDialog({})}>{tt('scans.newScan')}</Button>
                {unitPrice !== null && (
                  <span className="text-xs text-ink-secondary">{tt('scans.priceLine', { credits: credits(unitPrice) })}</span>
                )}
              </div>
            )}
          </header>
        </>
      )}

      {!scanId && notice && <NoticeBar notice={notice} onDismiss={() => setNotice(null)} />}
      {run.isPending && (
        <div role="status" className="flex items-center gap-2 text-sm text-ink-secondary">
          <Spinner className="h-4 w-4" /> {tt('scans.running')} {tt('scans.runSlow')}
        </div>
      )}

      {!scanId && (
        <>
          <ScopeNotice />

          {list.isError && (
            <ErrorNotice
              message={list.data ? tt('scans.refreshFailed') : tt('scans.loadFailed')}
              onRetry={() => void list.refetch()}
              retryLabel={tt('scans.retry')}
            />
          )}

          {list.isPending && <ListSkeleton />}

          {/* Tiles and lists only with real data: never zeros standing in for an error. */}
          {list.data && (
            <>
              {truncated && <p className="text-xs text-ink-secondary">{tt('scans.truncated', { count: PAGE_SIZE })}</p>}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile label={tt('scans.tile.domains')} value={tiles.domains} hint={tt('scans.tile.domainsHint')} />
                <StatTile label={tt('scans.tile.critical')} value={tiles.critical}
                          tone={tiles.critical > 0 ? 'critical' : undefined} hint={tt('scans.tile.criticalHint')} />
                <StatTile label={tt('scans.tile.upcoming')} value={tiles.upcoming} hint={tt('scans.tile.upcomingHint')} />
                <StatTile label={tt('scans.tile.failed')} value={tiles.failed}
                          tone={tiles.failed > 0 ? 'serious' : undefined} hint={tt('scans.tile.failedHint')} />
              </div>

              <SchedulesCard
                groups={groups}
                canSchedule={canSchedule}
                unitPrice={unitPrice}
                fmtDay={fmtDay}
                runningId={runningId}
                onRunNext={(s) => { setNotice(null); run.mutate(s); }}
                onCancel={(g) => setCancelGroup(g)}
              />

              <ScanList
                rows={rows}
                fmt={fmt}
                canSchedule={canSchedule}
                runningId={runningId}
                onOpen={openScan}
                onRun={(s) => { setNotice(null); run.mutate(s); }}
                onNew={() => setDialog({})}
              />
            </>
          )}
        </>
      )}

      {canSchedule && (
        <ScheduleScanModal
          open={dialog !== null}
          onClose={() => setDialog(null)}
          onScheduled={onScheduled}
          initialDomain={dialog?.domain}
          initialVendorId={dialog?.vendorId ?? null}
        />
      )}

      <CancelScheduleModal
        group={cancelGroup}
        unitPrice={unitPrice}
        pending={cancel.isPending}
        onClose={() => setCancelGroup(null)}
        onConfirm={(g) => cancel.mutate(g)}
      />
    </div>
  );
}

// --- Notices and scope ----------------------------------------------------------

function NoticeBar({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  const tt = useScanT();
  const ref = useRef<HTMLDivElement>(null);
  // The action that produced this notice may have been triggered far down the
  // page; bring the outcome into view so it is never missed.
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [notice]);

  if (notice.tone === 'error') {
    return (
      <div ref={ref}>
        <ErrorNotice message={notice.text} />
        {notice.detail && <p className="mt-1 text-xs text-ink-secondary break-words">{notice.detail}</p>}
        <button onClick={onDismiss} className="mt-1 text-xs font-medium text-brand hover:underline min-h-[44px]">
          {tt('scans.notice.dismiss')}
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} role="status" className="rounded-xl border border-good/30 bg-good/[0.06] px-4 py-3">
      <p className="text-sm text-ink">{notice.text}</p>
      <div className="mt-1 flex flex-wrap gap-x-4">
        {notice.action && (
          <button onClick={notice.action.onClick}
                  className="text-sm font-medium text-brand hover:underline min-h-[44px]">
            {notice.action.label}
          </button>
        )}
        <button onClick={onDismiss} className="text-sm text-ink-secondary hover:underline min-h-[44px]">
          {tt('scans.notice.dismiss')}
        </button>
      </div>
    </div>
  );
}

function ScopeNotice() {
  const tt = useScanT();
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl bg-surface-sunken px-4 py-3 flex items-start gap-3">
      <IconShield className="w-5 h-5 text-brand shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-ink">{tt('scans.scope.title')}</p>
        <p className="text-ink-secondary mt-1">{tt('scans.scope.doesNot')}</p>
        {open && (
          <div className="mt-2 space-y-2 text-ink-secondary">
            <p>{tt('scans.scope.does')}</p>
            <p>{tt('scans.scope.checkedList')}</p>
            <p>{tt('scans.scope.bareDomain')}</p>
          </div>
        )}
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-sm font-medium text-brand hover:underline min-h-[44px]"
        >
          {open ? tt('scans.scope.hide') : tt('scans.scope.show')}
        </button>
      </div>
    </div>
  );
}

// --- Schedules --------------------------------------------------------------------

interface ScheduleGroup {
  configId: string;
  domain: string;
  vendorName: string | null;
  total: number;
  completed: number;
  pending: ScanRow[];
  next: ScanRow;
  cadence: 'SINGLE' | 'QUARTERLY' | 'MONTHLY' | 'unknown';
}

/** Rebuild schedules from scan rows. A schedule is active while it has runs left. */
function buildGroups(rows: ScanRow[], truncated: boolean): ScheduleGroup[] {
  const byConfig = new Map<string, ScanRow[]>();
  for (const s of rows) {
    if (!s.configId) continue;
    byConfig.set(s.configId, [...(byConfig.get(s.configId) ?? []), s]);
  }
  const out: ScheduleGroup[] = [];
  for (const [configId, all] of byConfig) {
    const pending = all
      .filter((s) => s.status === 'PENDING')
      .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
    if (pending.length === 0) continue;
    const total = all.length;
    // Cadence is inferred from the run count, so only when nothing was cut off.
    const cadence = truncated ? 'unknown'
      : total === 1 ? 'SINGLE' : total === 4 ? 'QUARTERLY' : total === 12 ? 'MONTHLY' : 'unknown';
    out.push({
      configId,
      domain: all[0]!.domain,
      vendorName: all[0]!.vendor?.name ?? null,
      total,
      completed: all.filter((s) => s.status === 'READY').length,
      pending,
      next: pending[0]!,
      cadence,
    });
  }
  return out.sort((a, b) => a.next.scheduledFor.localeCompare(b.next.scheduledFor));
}

function SchedulesCard({ groups, canSchedule, unitPrice, fmtDay, runningId, onRunNext, onCancel }: {
  groups: ScheduleGroup[];
  canSchedule: boolean;
  unitPrice: number | null;
  fmtDay: (iso: string) => string;
  runningId: string | null;
  onRunNext: (s: ScanRow) => void;
  onCancel: (g: ScheduleGroup) => void;
}) {
  const tt = useScanT();
  void unitPrice;
  return (
    <Card>
      <CardHeader title={tt('scans.sched.title')} subtitle={tt('scans.sched.subtitle')} />
      {groups.length === 0 ? (
        <p className="text-sm text-ink-secondary">{tt('scans.sched.none')}</p>
      ) : (
        <ul>
          {groups.map((g) => {
            const due = isDue(g.next);
            return (
            <li key={g.configId} className="py-3 border-t border-line first:border-t-0 first:pt-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-sm font-medium text-ink break-all">{g.domain}</p>
                  <p className="text-xs text-ink-secondary mt-0.5">
                    {tt(`scans.sched.cadence.${g.cadence}` as ScanKey)}
                    {g.vendorName ? ` · ${tt('scans.list.supplier', { name: g.vendorName })}` : ''}
                    {' · '}{tt('scans.sched.progress', { done: g.completed, total: g.total })}
                    {' · '}{tt('scans.sched.remaining', { count: g.pending.length })}
                  </p>
                  <p className="text-xs text-ink-secondary">{tt('scans.sched.next', { date: fmtDay(g.next.scheduledFor) })}</p>
                </div>
                {canSchedule && (
                  <div className="flex flex-wrap gap-2">
                    {/* A run that is already due has its own "Run now" in the list below. */}
                    {!due && (
                      <Button
                        size="sm"
                        disabled={runningId !== null}
                        onClick={() => onRunNext(g.next)}
                      >
                        {runningId === g.next.id ? tt('scans.running') : tt('scans.sched.runNext')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      aria-label={tt('scans.sched.cancelAria', { domain: g.domain })}
                      onClick={() => onCancel(g)}
                    >
                      {tt('scans.sched.cancel')}
                    </Button>
                  </div>
                )}
              </div>
              {canSchedule && !due && (
                <p className="mt-1 text-xs text-ink-muted">{tt('scans.noChargeEarly', { date: fmtDay(g.next.scheduledFor) })}</p>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function CancelScheduleModal({ group, unitPrice, pending, onClose, onConfirm }: {
  group: ScheduleGroup | null;
  unitPrice: number | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (g: ScheduleGroup) => void;
}) {
  const tt = useScanT();
  const credits = useCredits();
  const count = group?.pending.length ?? 0;
  return (
    <Modal
      open={group !== null}
      onClose={onClose}
      title={tt('scans.cancel.title')}
      footer={
        <>
          <Button onClick={onClose}>{tt('scans.cancel.keep')}</Button>
          <Button variant="danger" disabled={pending || !group} onClick={() => group && onConfirm(group)}>
            {pending ? tt('scans.cancel.working') : tt('scans.cancel.confirm', { count })}
          </Button>
        </>
      }
    >
      {group && (
        <div className="space-y-3 text-sm text-ink">
          <p className="font-mono break-all">{group.domain}</p>
          <p>{tt('scans.cancel.body', { count, domain: group.domain })}</p>
          <p className="font-medium text-critical">
            {tt('scans.cancel.noRefund')}
            {unitPrice !== null && ` ${tt('scans.cancel.forfeit', { credits: credits(unitPrice * count) })}`}
          </p>
          <p className="text-ink-secondary">{tt('scans.cancel.kept')}</p>
        </div>
      )}
    </Modal>
  );
}

// --- List ---------------------------------------------------------------------------

function ListSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
      </div>
      <Skeleton className="h-32" />
      <Skeleton className="h-64" />
    </div>
  );
}

function ResultGlance({ scan }: { scan: ScanRow }) {
  const tt = useScanT();
  switch (scan.status) {
    case 'PENDING':
      return <span className="text-xs text-ink-secondary">{tt('scans.glance.waiting')}</span>;
    case 'IN_PROGRESS':
      return <span className="text-xs text-ink-secondary">{tt('scans.glance.running')}</span>;
    case 'FAILED':
      return <span className="text-xs text-ink-secondary">{tt('scans.glance.failed')}</span>;
    case 'READY': {
      const total = scan.redCount + scan.amberCount + scan.greenCount;
      if (total === 0) return <Pill tone="neutral">{tt('scans.glance.none')}</Pill>;
      return (
        <span className="flex flex-wrap items-center gap-1.5">
          {scan.redCount > 0 && <Pill tone="critical">{tt('scans.glance.critical', { count: scan.redCount })}</Pill>}
          {scan.amberCount > 0 && <Pill tone="warning">{tt('scans.glance.warning', { count: scan.amberCount })}</Pill>}
          {/* "No issues" is only claimed when the stored findings back it up. */}
          {scan.redCount === 0 && scan.amberCount === 0 && (scan.findingsJson || isExpired(scan)) && (
            <Pill tone="good">{tt('scans.glance.clean')}</Pill>
          )}
          {scan.redCount === 0 && scan.amberCount === 0 && !scan.findingsJson && !isExpired(scan) && (
            <Pill tone="neutral">{tt('scans.glance.noDetail')}</Pill>
          )}
          {scan.greenCount > 0 && (
            <span className="text-xs text-ink-secondary">{tt('scans.glance.low', { count: scan.greenCount })}</span>
          )}
          {isExpired(scan) && <span className="text-xs text-ink-secondary">· {tt('scans.glance.expired')}</span>}
        </span>
      );
    }
  }
}

function whenLabel(scan: ScanRow, fmt: (iso: string | null) => string, tt: ReturnType<typeof useScanT>) {
  if (scan.status === 'READY') return tt('scans.list.ranAt', { date: fmt(scan.completedAt ?? scan.scheduledFor) });
  if (scan.status === 'FAILED') return tt('scans.list.failedAt', { date: fmt(scan.completedAt ?? scan.startedAt ?? scan.scheduledFor) });
  if (scan.status === 'IN_PROGRESS') return tt('scans.list.startedAt', { date: fmt(scan.startedAt ?? scan.scheduledFor) });
  return tt('scans.list.dueAt', { date: fmt(scan.scheduledFor) });
}

function ScanList({ rows, fmt, canSchedule, runningId, onOpen, onRun, onNew }: {
  rows: ScanRow[];
  fmt: (iso: string | null) => string;
  canSchedule: boolean;
  runningId: string | null;
  onOpen: (id: string) => void;
  onRun: (s: ScanRow) => void;
  onNew: () => void;
}) {
  const tt = useScanT();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  // Future runs of a schedule live in the schedules card; this list is what has
  // happened or is waiting to.
  const visible = rows.filter((s) => s.status !== 'PENDING' || isDue(s));

  const shown = visible
    .filter((s) => !status || s.status === status)
    .filter((s) => {
      const q = search.trim().toLowerCase();
      return !q || s.domain.toLowerCase().includes(q) || (s.vendor?.name.toLowerCase().includes(q) ?? false);
    })
    .sort((a, b) =>
      (b.completedAt ?? b.startedAt ?? b.scheduledFor).localeCompare(a.completedAt ?? a.startedAt ?? a.scheduledFor));

  return (
    <Card>
      <CardHeader title={tt('scans.list.title')} />

      {visible.length === 0 ? (
        <EmptyState
          title={tt('scans.list.noneTitle')}
          description={tt('scans.list.noneBody')}
          action={canSchedule ? <Button variant="primary" onClick={onNew}>{tt('scans.newScan')}</Button> : undefined}
        />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              className="oc-input sm:max-w-xs"
              type="search"
              placeholder={tt('scans.list.search')}
              aria-label={tt('scans.list.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="oc-input sm:w-auto"
              aria-label={tt('scans.list.filterStatus')}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">{tt('scans.list.all')}</option>
              {(['READY', 'PENDING', 'IN_PROGRESS', 'FAILED'] as const).map((s) => (
                <option key={s} value={s}>{tt(`scans.status.${s}` as ScanKey)}</option>
              ))}
            </select>
          </div>

          {shown.length === 0 ? (
            <p className="text-sm text-ink-secondary py-6 text-center">{tt('scans.list.noMatch')}</p>
          ) : (
            <ul>
              {shown.map((s) => (
                <li key={s.id} className="py-2 border-t border-line first:border-t-0">
                  <button
                    type="button"
                    onClick={() => onOpen(s.id)}
                    aria-label={`${tt('scans.list.open')}: ${s.domain}`}
                    className="w-full text-left flex items-start gap-3 min-h-[44px] py-1"
                  >
                    <span className="min-w-0 flex-1 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
                      <span className="min-w-0 sm:flex-1">
                        <span className="block font-mono text-sm font-medium text-ink break-all">{s.domain}</span>
                        <span className="block text-xs text-ink-secondary mt-0.5">
                          {s.vendor ? tt('scans.list.supplier', { name: s.vendor.name }) : tt('scans.list.own')}
                          {' · '}{whenLabel(s, fmt, tt)}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-2 sm:justify-end">
                        <Pill tone={STATUS_TONE[s.status]}>{tt(`scans.status.${s.status}` as ScanKey)}</Pill>
                        <ResultGlance scan={s} />
                      </span>
                    </span>
                    <IconChevron className="w-4 h-4 mt-1 shrink-0 text-ink-muted" aria-hidden />
                  </button>
                  {canSchedule && (
                    <RunControls scan={s} runningId={runningId} onRun={onRun} fmt={fmt} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** Run / retry controls with the cost statement that goes with them. */
function RunControls({ scan, runningId, onRun, fmt }: {
  scan: ScanRow;
  runningId: string | null;
  onRun: (s: ScanRow) => void;
  fmt: (iso: string | null) => string;
}) {
  const tt = useScanT();
  const busy = runningId === scan.id;
  const stuck = isStuck(scan);

  let label: string | null = null;
  let hint = '';
  if (scan.status === 'PENDING') {
    label = tt('scans.run');
    hint = tt('scans.noCharge');
  } else if (scan.status === 'FAILED') {
    label = tt('scans.retry');
    hint = tt('scans.noChargeRetry');
  } else if (stuck) {
    label = tt('scans.runAgain');
    hint = tt('scans.stuck', { date: fmt(scan.startedAt) });
  }
  if (!label) return null;

  return (
    <div className="pl-0 pb-1 flex flex-col sm:flex-row sm:items-center gap-x-3 gap-y-1">
      <Button size="sm" disabled={runningId !== null} onClick={() => onRun(scan)}>
        {busy ? tt('scans.running') : label}
      </Button>
      <span className="text-xs text-ink-secondary">{hint}</span>
    </div>
  );
}

// --- Detail ---------------------------------------------------------------------------

function ScanDetailView({ id, fmt, canSchedule, onBack, onRun, runningId, onScanAgain }: {
  id: string;
  fmt: (iso: string | null) => string;
  canSchedule: boolean;
  onBack: () => void;
  onRun: (s: ScanRow) => void;
  runningId: string | null;
  onScanAgain: (s: ScanRow) => void;
}) {
  const tt = useScanT();
  const describeError = useScanError();

  const detail = useQuery({
    queryKey: ['scans', 'detail', id],
    queryFn: () => api.get<ScanDetail>(`/scans/${id}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    refetchInterval: (q) => (q.state.data?.status === 'IN_PROGRESS' ? 4000 : false),
  });

  const back = (
    <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
      <IconChevron className="w-4 h-4 rotate-180" aria-hidden /> {tt('scans.detail.back')}
    </Button>
  );

  if (detail.isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        {back}
        <Skeleton className="h-10 w-2/3" />
        <div className="grid grid-cols-3 gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.code === 'SCAN_NOT_FOUND';
    return (
      <div className="space-y-4">
        {back}
        <ErrorNotice
          message={notFound ? tt('scans.detail.notFound') : `${tt('scans.detail.loadFailed')} (${describeError(detail.error)})`}
          onRetry={notFound ? undefined : () => void detail.refetch()}
          retryLabel={tt('scans.retry')}
        />
      </div>
    );
  }

  const scan = detail.data;
  const findings = scan.findings;
  const vendorName = scan.vendor?.name ?? null;
  const counts = findings.length > 0
    ? countBySeverity(findings)
    : { critical: scan.redCount, warning: scan.amberCount, low: scan.greenCount };
  const storedTotal = scan.redCount + scan.amberCount + scan.greenCount;

  return (
    <div className="space-y-5">
      {back}

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1 font-mono break-all">{scan.domain}</h1>
          <p className="oc-muted mt-1">
            {vendorName ? tt('scans.list.supplier', { name: vendorName }) : tt('scans.list.own')}
            {' · '}{whenLabel(scan, fmt, tt)}
          </p>
          {scan.status === 'READY' && scan.reportExpiresAt && !scan.reportExpired && (
            <p className="text-xs text-ink-secondary mt-0.5">
              {tt('scans.detail.availableUntil', { date: fmt(scan.reportExpiresAt) })}
            </p>
          )}
        </div>
        <Pill tone={STATUS_TONE[scan.status]}>{tt(`scans.status.${scan.status}` as ScanKey)}</Pill>
      </header>

      <ScopeNotice />

      {(scan.status === 'PENDING' || scan.status === 'FAILED' || isStuck(scan)) && canSchedule && (
        <RunControls scan={scan} runningId={runningId} onRun={onRun} fmt={fmt} />
      )}

      {scan.status === 'PENDING' && (
        <InfoBlock>{tt('scans.detail.pending', { date: fmt(scan.scheduledFor) })}</InfoBlock>
      )}
      {scan.status === 'IN_PROGRESS' && <InfoBlock>{tt('scans.detail.running')}</InfoBlock>}

      {scan.status === 'FAILED' && (
        <div className="space-y-2">
          <ErrorNotice message={tt('scans.detail.failed')} />
          {scan.errorMessage && (
            <p className="text-xs text-ink-secondary break-words">
              {tt('scans.notice.reason', { reason: scan.errorMessage })}
            </p>
          )}
        </div>
      )}

      {scan.status === 'READY' && (
        <>
          {/* Counts are shown only when they describe something real. */}
          {storedTotal > 0 && (
            <section aria-label={tt('scans.detail.summary')} className="grid grid-cols-3 gap-3">
              <StatTile label={tt('scans.detail.tile.critical')} value={counts.critical}
                        tone={counts.critical > 0 ? 'critical' : undefined} />
              <StatTile label={tt('scans.detail.tile.warning')} value={counts.warning} />
              <StatTile label={tt('scans.detail.tile.low')} value={counts.low}
                        hint={tt('scans.detail.tile.lowHint')} />
            </section>
          )}

          {scan.reportExpired && <ErrorNotice message={tt('scans.detail.expired')} />}

          {!scan.reportExpired && findings.length === 0 && (
            <ErrorNotice
              message={storedTotal > 0
                ? tt('scans.detail.unreadable', { count: storedTotal })
                : tt('scans.detail.noneRecorded')}
            />
          )}

          {findings.length > 0 && (
            <section className="space-y-3">
              <h2 className="oc-h2">{tt('scans.detail.findings')}</h2>
              <FindingsList findings={findings} vendorName={vendorName} />
            </section>
          )}
        </>
      )}

      {canSchedule && (scan.status === 'READY' || scan.status === 'FAILED') && (
        <div className="pt-2 border-t border-line">
          <Button onClick={() => onScanAgain(scan)}>{tt('scans.scanAgain')}</Button>
          <p className="mt-1 text-xs text-ink-secondary">{tt('scans.scanAgainCost')}</p>
        </div>
      )}
    </div>
  );
}

function InfoBlock({ children }: { children: ReactNode }) {
  return (
    <div className={cn('rounded-xl border border-line bg-surface-sunken px-4 py-3 text-sm text-ink')}>
      {children}
    </div>
  );
}
